import * as React from 'react';
import { useLeapState } from '../hooks/useLeapState';
import { useLeapData } from '../hooks/useLeapData';

import Content, { Filter, Result, Item } from '../components/blueprints/core/Content';
import Grid from '../components/controls/grid/Grid';
import Section from '../components/controls/grid/Section';
import Row from '../components/controls/grid/Row';
import Separator from '../components/controls/grid/Separator';

import Text from '../components/controls/Text';
import Option from '../components/controls/Option';
import SearchBox from '../components/controls/SearchBox';

import CorporationResultList from '../features/corporation/CorporationResultList';
import { useBoolean, useId } from '@uifabric/react-hooks';
import { useSecurityRoles } from '../hooks/useSecurityRoles';
import { PrimaryButton, Dialog, DialogFooter, DefaultButton, DialogType } from '@fluentui/react';
import { AppDispatch } from '../app/store';
import { useDispatch, useSelector } from 'react-redux';
import { itemData } from '../features/operation/thunks/itemThunk';
import { security } from '../features/security/thunks/securityThunk';
import { setResponse } from '../features/response/responseSlice';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../api/client';
import { setOperation } from '../features/operation/operationSlice';

export const RegeneratePassword = createAsyncThunk(
    'item/RegeneratePassword',
    async (args: { token: string | undefined, page: any }) => {
        const response = await client.post("Corporation", { form: {}, page: args.page }, 'RegeneratePassword', args.token)
        return response
    }
)

export const GenerateUser = createAsyncThunk(
    'item/GenerateUser',
    async (args: { token: string | undefined, page: any }) => {
        const response = await client.post("Corporation", { form: {}, page: args.page }, 'GenerateUser', args.token)
        return response
    }
)

export default () => {
    const controller = "Corporation"
    const roles = useSecurityRoles()
    const isCorporation: boolean = roles.isCorporation;
        
    const page = useLeapState(controller, roles.role !== undefined, isCorporation? "item" : "filter")

    const code = useLeapData("code", "codice", page)
    const businessName = useLeapData("businessName", "ragione sociale", page)
    const leap_CorporationStateID = useLeapData("leap_CorporationStateID", "attivo", page, "corporationStateOption")
    const effectiveDateOfActivity = useLeapData("effectiveDateOfActivity", "decorrenza attività", page)
    const terminationDate = useLeapData("terminationDate", "data cessazione", page)
    const activeEmployees = useLeapData("activeEmployees", "dipendenti attivi", page)

    //dati referente
    const referentFirstname = useLeapData("referentFirstname", "nome referente", page)
    const referentLastname = useLeapData("referentLastname", "cognome referente", page)
    const telephone = useLeapData("telephone", "telefono", page)
    const mobilephone = useLeapData("mobilephone", "cellulare", page)

    //dati azienda
    const email = useLeapData("email", "email", page)
    const pec = useLeapData("pec", "pec", page)
    const address = useLeapData("address", "indirizzo", page)
    const streetNumber = useLeapData("streetNumber", "civico", page)
    const postalCode = useLeapData("postalCode", "cap", page)
    const nationID = useLeapData("nationID", "nazione", page, "nations")
    const regionID = useLeapData("regionID", "regione", page, "regions")
    const provinceID = useLeapData("provinceID", "provincia", page, "provinces")
    const municipalityID = useLeapData("municipalityID", "comune", page, "municipalities")

    //estremi bancari
    const cf = useLeapData("cf", "codice fiscale", page)
    const piva = useLeapData("piva", "partita iva", page)
    const sdi = useLeapData("sdi", "SDI", page)
    const iban = useLeapData("iban", "IBAN", page)
    const headerCC = useLeapData("headerCC", "intestazione C/C", page)

    const expired: boolean = page?.item?.values?.terminationDate?.value !== null
        && new Date(page?.item?.values?.terminationDate?.value).getTime() < new Date().getTime();

    const isONHC: boolean = roles.isONHCAccount || roles.isONHCAdmin || roles.isONHCMaster || roles.isONHCOperator;
    const hasUser: string = page?.item?.state?.data?.hasUser;

    const [hideDialogRegeneratePassword, { toggle: toggleHideDialogRegeneratePassword }] = useBoolean(true);
    const [hideDialogGenerateUser, { toggle: togglehideDialogGenerateUser }] = useBoolean(true);

    const labelId: string = useId('dialogLabel');
    const subTextId: string = useId('subTextLabel');
    const dialogStyles = { main: { maxWidth: 450 } };

    const modalProps = React.useMemo(
        () => ({
            titleAriaId: labelId,
            subtitleAriaId: subTextId,
            isBlocking: false,
            styles: dialogStyles,
        }),
        [labelId, subTextId, dialogStyles],
    );
    const dialogContentPropsRegeneratePassword = {
        type: DialogType.normal,
        title: 'Richiesta conferma',
        closeButtonAriaLabel: 'Chiudi',
        subText: 'Procedo alla rigenerazione della password?',
    };

    const dialogContentPropsGenerateUser = {
        type: DialogType.normal,
        title: 'Richiesta conferma',
        closeButtonAriaLabel: 'Chiudi',
        subText: 'Procedo alla generazione dell\'utente?',
    };

    const dispatch: AppDispatch = useDispatch();
    const iData = useSelector(itemData);
    const dItem = iData.find(d => d.key === controller)?.value
    const metadataPage = dItem?.metadata?.page
    const credential = useSelector(security)?.credential

    const dispatchReload = (response: any) => {
        dispatch(setOperation("reload", controller))
        return response
    }
    const dispatchMessage = (response: any) => {
        dispatch(setResponse(response.payload.response.message, response.payload.response.level))
    }

    const onExecuteRegeneratePassword = () => {
        toggleHideDialogRegeneratePassword()
        dispatch(RegeneratePassword({ token: credential.token, page: metadataPage }))
            .then((response: any) => {
                dispatch(setResponse(response.payload.response.message, response.payload.response.level))
                return response
            })
            .then(dispatchReload)
            .then(dispatchMessage)
    }
    const onExecuteGenerateUser = () => {
        togglehideDialogGenerateUser()
        dispatch(GenerateUser({ token: credential.token, page: metadataPage }))
            .then((response: any) => {
                dispatch(setResponse(response.payload.response.message, response.payload.response.level))
                return response
            })
            .then(dispatchReload)
            .then(dispatchMessage)
    }

    return <>
        <Content title={isCorporation ? "Il mio Profilo" : "Azienda"} controller={controller}>
            <Filter>
                <Grid>
                    <Section toolbar="filter">
                        <Row>
                            <Text {...code} className="ms-sm3" maxlength={12} />
                            <Text {...businessName} className="ms-sm5" />
                            <Option {...leap_CorporationStateID} className="ms-sm3" type="horizontal" />
                        </Row>
                        <Row>
                            <SearchBox {...nationID} className="ms-sm3" />
                            <SearchBox {...regionID} className="ms-sm3" />
                        </Row>
                        <Row>
                            <SearchBox {...provinceID} className="ms-sm3" />
                            <SearchBox {...municipalityID} className="ms-sm3" />
                        </Row>
                    </Section>
                </Grid>
            </Filter>
            <Result>
                <Grid>
                    <Section toolbar="result" controller={controller}>
                        <CorporationResultList controller={controller} items={page.result?.state?.data} />
                    </Section>
                </Grid>
            </Result>
            <Item>
                <Grid>
                    <Section toolbar="item" controller={controller}>
                        <Row>
                            <Text {...code} className="ms-sm3" readOnly maxlength={12} />
                            <Text {...businessName} className="ms-sm5" required readOnly={isCorporation} />
                            <Option {...leap_CorporationStateID} className="ms-sm3" type="horizontal" disabled={expired || isCorporation}/>
                        </Row>
                        <Row>
                            <Text {...effectiveDateOfActivity} className="ms-sm2" type="calendar" readOnly />
                            <Text {...terminationDate} className="ms-sm2" type="calendar" dateRange={{ minDate: Date().toLocaleString(), maxDate: undefined }} readOnly={isCorporation}/>
                            <Separator className="ms-sm1"></Separator>
                            <Text {...activeEmployees} className="ms-sm1" readOnly hidden={page.modality === "new"} />
                            {page.modality === "item" && isONHC && hasUser && (
                                <>
                                    <PrimaryButton text="Resetta password" onClick={toggleHideDialogRegeneratePassword} style={{ marginTop: "20px", marginLeft: "25px" }} />
                                    <Dialog hidden={hideDialogRegeneratePassword} onDismiss={toggleHideDialogRegeneratePassword} dialogContentProps={dialogContentPropsRegeneratePassword} modalProps={modalProps} >
                                        <DialogFooter>
                                            <PrimaryButton onClick={onExecuteRegeneratePassword} text="Resetta password" />
                                            <DefaultButton onClick={toggleHideDialogRegeneratePassword} text="Annulla" />
                                        </DialogFooter>
                                    </Dialog>
                                </>
                            )}
                            {page.modality === "item" && isONHC && !hasUser && (
                                <>
                                    <PrimaryButton text="Genera Utente" onClick={togglehideDialogGenerateUser} style={{ marginTop: "20px", marginLeft: "25px" }} />
                                    <Dialog hidden={hideDialogGenerateUser} onDismiss={togglehideDialogGenerateUser} dialogContentProps={dialogContentPropsGenerateUser} modalProps={modalProps} >
                                        <DialogFooter>
                                            <PrimaryButton onClick={onExecuteGenerateUser} text="Genera Utente" />
                                            <DefaultButton onClick={togglehideDialogGenerateUser} text="Annulla" />
                                        </DialogFooter>
                                    </Dialog>
                                </>
                            )}
                        </Row>
                        <Section title="dati referente">
                            <Row>
                                <Text {...referentFirstname} className="ms-sm3" />
                                <Text {...referentLastname} className="ms-sm3" />
                            </Row>
                            <Row>
                                <Text {...telephone} className="ms-sm3" />
                                <Text {...mobilephone} className="ms-sm3" />
                            </Row>
                        </Section>
                        <Section title="dati azienda">
                            <Row>
                                <Text {...email} className="ms-sm3" />
                                <Text {...pec} className="ms-sm3" />
                            </Row>
                            <Row>
                                <Text {...address} className="ms-sm3" />
                                <Text {...streetNumber} className="ms-sm2" maxlength={8} />
                                <Text {...postalCode} className="ms-sm1" maxlength={5} />
                            </Row>
                            <Row>
                                <SearchBox {...nationID} className="ms-sm5" />
                                {/*<ComboBox {...provinceID} className="ms-sm5" children={[{ name: "municipalityID", lookup: "municipalities", controller: controller }]} disabled={!(nationSelected)} />*/}
                                <SearchBox {...municipalityID} className="ms-sm5" />
                            </Row>
                            <Row>
                                <Text {...cf} className="ms-sm3" readOnly={isCorporation} />
                                <Text {...piva} className="ms-sm3" readOnly={isCorporation}/>
                                <Text {...sdi} className="ms-sm3" />
                            </Row>
                        </Section>
                        <Section title="estremi bancari">
                            <Row>
                                <Text {...iban} className="ms-sm3" />
                                <Text {...headerCC} className="ms-sm3" />
                            </Row>
                        </Section>
                    </Section>
                </Grid>
            </Item>
        </Content>
    </>
};
